import { useState } from "react";
import { ArrowUpRight, Target, Trees, Coffee, Flag, MapPin, Navigation, Plus } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Kicker } from "@/components/Section";
import TiltCard from "@/components/TiltCard";
import Reveal from "@/components/Reveal";
import Slideshow from "@/components/Slideshow";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const HERO_IMG = "https://images.unsplash.com/photo-1776717163992-1919b844d715?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const RANGE_IMG = "https://customer-assets.emergentagent.com/job_web-editor-45/artifacts/3g1m5zlk_ChatGPT%20Image%2024.%20Juni%202026%2C%2022_55_28.png";
const CLUB_IMG = "https://customer-assets.emergentagent.com/job_web-editor-45/artifacts/0b8e4gxb_ChatGPT%20Image%2025.%20Juni%202026%2C%2003_04_58.png";

// Echte Golfclub-Fotos für die Diashow
const RANGE_SLIDES = [
  { src: "https://customer-assets.emergentagent.com/job_web-editor-45/artifacts/jnvsis7b_ChatGPT%20Image%2027.%20Juni%202026%2C%2000_43_36.png", alt: "Golfer auf dem Fairway mit Clubhaus im Hintergrund" },
  { src: "https://customer-assets.emergentagent.com/job_web-editor-45/artifacts/jsm2m50b_ChatGPT%20Image%2027.%20Juni%202026%2C%2000_48_44.png", alt: "Clubhaus des GC Kirchheim-Wendlingen mit Fahnen" },
  { src: "https://customer-assets.emergentagent.com/job_web-editor-45/artifacts/4p0m442n_ChatGPT%20Image%2027.%20Juni%202026%2C%2000_50_26.png", alt: "Teich mit Fontäne im Abendlicht" },
  { src: "https://customer-assets.emergentagent.com/job_web-editor-45/artifacts/rhkudh8f_ChatGPT%20Image%2027.%20Juni%202026%2C%2001_07_01.png", alt: "Grün mit gelber Fahne und Bergpanorama" },
  { src: "https://customer-assets.emergentagent.com/job_web-editor-45/artifacts/nia796i3_ChatGPT%20Image%2027.%20Juni%202026%2C%2001_08_41.png", alt: "Golfplatz mit Teich, Fontäne und Clubhaus" },
];
const icons = [Target, Trees, Coffee, Flag];
const featureImgs = [RANGE_IMG, HERO_IMG, CLUB_IMG, RANGE_IMG];
const MAPS_QUERY = "Golfclub+Kirchheim-Wendlingen+Am+Schulerberg+1+73230+Kirchheim+unter+Teck";
const MAPS_EMBED = `https://www.google.com/maps?q=${MAPS_QUERY}&z=14&output=embed`;
const MAPS_DIR = `https://www.google.com/maps/dir/?api=1&destination=${MAPS_QUERY}`;

const Golfclub = () => {
  const { t, lang } = useLang();
  const g = t.golfclub;
  const de = lang === "de";
  const href = `https://${g.ctaUrl}`;
  const [activeFeature, setActiveFeature] = useState(null);
  const infoLabel = de ? "Mehr erfahren" : "Learn more";

  return (
    <div data-testid="golfclub-page">
      {/* VIDEO INTRO + HERO HEADING */}
      <section className="relative w-full bg-[#050505] pt-20" data-testid="golfclub-hero-video-section">
        <div className="relative w-full overflow-hidden">
          <video
            data-testid="golfclub-hero-video"
            className="w-full h-auto object-cover block"
            src="/hero-intro.mp4"
            poster="/hero-poster.jpg"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-transparent pointer-events-none" />
        </div>
      </section>

      {/* HEADING unter dem Video */}
      <section className="pt-12 md:pt-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="golfclub-heading">
        <Reveal direction="up">
          <Kicker>{g.kicker}</Kicker>
          <h1 className="font-heading text-5xl sm:text-6xl md:text-8xl uppercase tracking-tight leading-none text-white mt-4">
            Golfclub<br /><span className="text-3xl sm:text-4xl md:text-6xl">Kirchheim-Wendlingen e.V.</span>
          </h1>
        </Reveal>
      </section>

      {/* FEATURE TILES — direkt unter dem Video */}
      <section className="py-16 md:py-24 border-b border-white/10" data-testid="golfclub-features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {g.features.map((f, i) => {
              const Icon = icons[i % icons.length];
              return (
                <Reveal key={f.t} direction="up" delay={(i % 4) * 0.06}>
                <TiltCard
                  data-testid={`golfclub-feature-${i}`}
                  className="bg-[#0F0F0F] border border-white/10 p-8 hover:border-[#ff0033]/60 transition-colors group flex flex-col h-full"
                >
                  <Icon size={36} className="text-[#ff0033]" />
                  <h3 className="font-heading text-3xl md:text-4xl uppercase tracking-tight text-white mt-6 leading-none group-hover:text-[#ff0033] transition-colors">
                    {f.t}
                  </h3>
                  <p className="text-neutral-300 text-base md:text-lg mt-4 leading-relaxed flex-1">{f.d}</p>
                  <button
                    onClick={() => setActiveFeature({ ...f, img: featureImgs[i], points: i === 0 ? g.rangePoints : null })}
                    data-testid={`golfclub-feature-info-${i}`}
                    className="mt-7 inline-flex items-center gap-2 self-start border border-white/20 text-white font-heading text-sm uppercase tracking-widest px-5 py-3 hover:border-[#ff0033] hover:text-[#ff0033] transition-colors"
                  >
                    <Plus size={16} /> {infoLabel}
                  </button>
                </TiltCard>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* DRIVING RANGE */}
      <section className="py-20 md:py-28 border-b border-white/10" data-testid="driving-range-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal direction="up" className="mb-10">
            <Slideshow slides={RANGE_SLIDES} />
          </Reveal>

          <Reveal direction="up" className="mb-10">
            <Kicker>// ÜBUNGSANLAGE</Kicker>
            <h2 className="font-heading text-5xl md:text-7xl uppercase tracking-tight text-white mt-3 leading-none">
              {g.rangeTitle}
            </h2>
          </Reveal>

          <Reveal direction="up" delay={0.1} className="mt-10 bg-[#0F0F0F] border-2 border-[#ff0033] p-8 md:p-10" data-testid="range-info-box" style={{ boxShadow: "0 0 28px rgba(255,0,51,0.25)" }}>
            <p className="text-neutral-300 text-base md:text-lg leading-relaxed">{g.rangeIntro}</p>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {g.rangePoints.map((p, i) => (
                <li key={`range-${i}`} className="flex items-start gap-3 text-neutral-200">
                  <Target size={16} className="text-[#ff0033] shrink-0 mt-1" />
                  <span className="text-sm md:text-base">{p}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* CONTENT auf Golfrasen-Bild */}
      <section className="relative overflow-hidden border-b border-white/10" data-testid="golfclub-content-section">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#050505]/85" />
        </div>
        <div className="relative z-10 py-20 md:py-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal direction="up" as="p" className="text-neutral-200 text-base md:text-lg max-w-3xl leading-relaxed">{g.intro}</Reveal>

          <Reveal direction="up" delay={0.1} className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7">
              <a
                href={href}
                target="_blank"
                rel="noreferrer"
                data-testid="golfclub-cta"
                className="inline-flex items-center gap-3 bg-[#ff0033] text-white font-heading uppercase tracking-wider px-8 py-5 text-lg hover:bg-[#cc0029] transition-colors"
              >
                {g.cta} · {g.ctaUrl} <ArrowUpRight size={22} />
              </a>
            </div>
            <div className="lg:col-span-5">
              <div className="bg-[#0F0F0F]/80 backdrop-blur-md border border-white/10 p-8">
                <div className="flex items-center gap-3 mb-3">
                  <MapPin size={18} className="text-[#ff0033]" />
                  <span className="font-mono-lab text-xs tracking-[0.2em] text-neutral-500 uppercase">{g.addressTitle}</span>
                </div>
                <p className="text-neutral-200 whitespace-pre-line leading-relaxed">{g.address}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Google Maps banner */}
      <section className="pb-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="golfclub-map">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
          <div>
            <Kicker>// {g.mapTitle.toUpperCase()}</Kicker>
            <p className="text-neutral-400 text-sm md:text-base mt-2">{g.mapHint}</p>
          </div>
          <a
            href={MAPS_DIR}
            target="_blank"
            rel="noreferrer"
            data-testid="golfclub-route-cta"
            className="inline-flex items-center gap-3 bg-[#ff0033] text-white font-heading uppercase tracking-wider px-8 py-4 text-lg hover:bg-[#cc0029] transition-colors"
          >
            <Navigation size={20} fill="currentColor" /> {g.mapCta}
          </a>
        </div>

        <a
          href={MAPS_DIR}
          target="_blank"
          rel="noreferrer"
          aria-label={g.mapCta}
          className="relative block border border-white/10 overflow-hidden group"
        >
          <iframe
            title="Golfclub Kirchheim-Wendlingen map"
            src={MAPS_EMBED}
            className="w-full h-[360px] md:h-[460px] grayscale contrast-125 pointer-events-none"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
          <div className="absolute inset-0 bg-[#ff0033]/0 group-hover:bg-[#ff0033]/10 transition-colors" />
          <div className="absolute bottom-5 left-5 inline-flex items-center gap-2 bg-[#050505]/90 border border-white/10 px-4 py-2.5">
            <Navigation size={16} className="text-[#ff0033]" />
            <span className="font-mono-lab text-xs tracking-[0.15em] text-white uppercase">{g.mapCta}</span>
          </div>
        </a>
      </section>

      {/* Feature info popup */}
      <Dialog open={!!activeFeature} onOpenChange={(o) => !o && setActiveFeature(null)}>
        <DialogContent className="max-w-2xl bg-[#0B0B0B] border-2 border-[#ff0033] text-white max-h-[88vh] overflow-y-auto" data-testid="golfclub-feature-modal" style={{ boxShadow: "0 0 32px rgba(255,0,51,0.3)" }}>
          {activeFeature && (
            <div>
              <DialogTitle className="sr-only">{activeFeature.t}</DialogTitle>
              <Kicker>// {g.kicker.replace("// ", "")}</Kicker>
              <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-tight text-white mt-3 leading-none">
                {activeFeature.t}
              </h2>
              <p className="text-neutral-300 text-base md:text-lg mt-5 leading-relaxed">
                {activeFeature.long || activeFeature.d}
              </p>
              {activeFeature.points && (
                <div className="mt-6 bg-[#0F0F0F] border-2 border-[#ff0033] p-6 md:p-7" style={{ boxShadow: "0 0 24px rgba(255,0,51,0.22)" }}>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                    {activeFeature.points.map((p, i) => (
                      <li key={`modal-range-${i}`} className="flex items-start gap-3 text-neutral-200">
                        <Target size={16} className="text-[#ff0033] shrink-0 mt-1" />
                        <span className="text-sm md:text-base">{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {activeFeature.img && (
                <div className="mt-6 border border-white/10 overflow-hidden">
                  <img
                    src={activeFeature.img}
                    alt={activeFeature.t}
                    className="w-full h-auto block"
                    data-testid="golfclub-feature-modal-img"
                  />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Golfclub;
